/**
 * React-free helpers behind an action's checklist rows.
 *
 * `checklist` is free-form JSONB in coms, which has two consequences this
 * module exists to handle. Nothing validates it on write, so the shape has to
 * be pinned down on read; and nothing strips unknown keys, so notes and file
 * attachments persist without a migration.
 *
 * Items have been written by several generations of this app and by the
 * assistant's task generator, so a stored row may be a bare string, or carry
 * `text`/`name` instead of `label`, or `status`/`completed` instead of
 * `checked`.
 */

import {
  commentsForSubmit,
  normalizeComments,
  type TaskComment,
} from './actionTicket/actionTicketModel';

/**
 * A file-manager document or folder attached to one checklist item. Legacy rows
 * are file-only (`fileId` + `label`); HWCG can also store folders via `kind:
 * 'folder'` + `folderId`.
 */
export type ChecklistFileAttachment = {
  kind?: 'file' | 'folder';
  fileId?: string;
  folderId?: string;
  label: string;
};

export const checklistAttachmentKey = (
  attachment: ChecklistFileAttachment
): string =>
  attachment.kind === 'folder'
    ? `folder:${attachment.folderId ?? ''}`
    : `file:${attachment.fileId ?? ''}`;

/**
 * Same values as `ACTION_STATUS_OPTIONS`. Kept here so this module stays free
 * of the form catalog that file imports.
 */
export const CHECKLIST_ITEM_STATUSES = [
  'Planned',
  'Current/Ongoing',
  'Blocked',
  'Submitted for Review',
  'Closed',
] as const;

export type ChecklistItemStatus = (typeof CHECKLIST_ITEM_STATUSES)[number];

export const DEFAULT_CHECKLIST_STATUS: ChecklistItemStatus = 'Planned';

/** HWCG Prerequisite Checklist picker labels (stored as Planned / Closed). */
export const HWCG_CHECKLIST_STATUS_OPTIONS = [
  { label: 'Incomplete', value: 'Planned' as const },
  { label: 'Complete', value: 'Closed' as const },
] as const;

export type HwcgChecklistStatusLabel = 'Incomplete' | 'Complete';

export const hwcgChecklistStatusLabel = (
  status: ChecklistItemStatus
): HwcgChecklistStatusLabel =>
  status === 'Closed' ? 'Complete' : 'Incomplete';

export const hwcgChecklistStatusFromLabel = (
  label: string
): ChecklistItemStatus =>
  label === 'Complete' || label === 'Closed' ? 'Closed' : 'Planned';

const isChecklistStatus = (value: string): value is ChecklistItemStatus =>
  (CHECKLIST_ITEM_STATUSES as readonly string[]).includes(value);

export type ActionChecklistItem = {
  label: string;
  /** Kept in step with `status === 'Closed'` so older readers still see done. */
  checked: boolean;
  status: ChecklistItemStatus;
  /** What happened on this step. Written under HWCG, read everywhere. */
  note?: string;
  attachments?: ChecklistFileAttachment[];
  /** HWCG discussion under this step. Always an array once normalized. */
  comments: TaskComment[];
  /**
   * The person responsible for this sub-task. Stored as a user guid.
   * Independent of the task-level Responsible Individuals.
   */
  responsible_individual_id?: string;
};

/** Guards a paste or a runaway generator from filling the column. */
export const MAX_CHECKLIST_FILES = 10;

const text = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const asRows = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

export function normalizeChecklistAttachments(
  value: unknown
): ChecklistFileAttachment[] {
  const out: ChecklistFileAttachment[] = [];
  for (const row of asRows(value)) {
    if (!row || typeof row !== 'object') continue;
    const entry = row as Record<string, unknown>;
    const label = text(entry.label) || text(entry.name);
    const folderId = text(entry.folderId);
    const fileId = text(entry.fileId) || text(entry.id);
    if (entry.kind === 'folder' || (folderId && !fileId)) {
      if (!folderId) continue;
      out.push({ kind: 'folder', folderId, label });
      continue;
    }
    if (!fileId) continue;
    out.push({ kind: 'file', fileId, label });
  }
  return out.slice(0, MAX_CHECKLIST_FILES);
}

/**
 * One stored row as the UI wants it.
 *
 * `note` and `attachments` are carried through even when the writing surface
 * cannot edit them, so saving an action with HWCG off cannot quietly drop what
 * someone recorded with it on.
 */
/**
 * A stored action status wins. Older rows only have a boolean, or a legacy
 * `Complete` / `Closed` string, and those become Closed or Planned.
 */
export function checklistItemStatus(row: {
  status?: string;
  checked?: boolean;
  completed?: boolean;
}): ChecklistItemStatus {
  const status = text(row.status);
  if (isChecklistStatus(status)) return status;
  // HWCG UI labels persisted by older clients, or typed in by hand.
  if (status === 'Complete' || status === 'Incomplete') {
    if (typeof row.checked === 'boolean') {
      return row.checked ? 'Closed' : DEFAULT_CHECKLIST_STATUS;
    }
    if (typeof row.completed === 'boolean') {
      return row.completed ? 'Closed' : DEFAULT_CHECKLIST_STATUS;
    }
    return status === 'Complete' ? 'Closed' : DEFAULT_CHECKLIST_STATUS;
  }
  const checked =
    typeof row.checked === 'boolean'
      ? row.checked
      : typeof row.completed === 'boolean'
        ? row.completed
        : status === 'Complete' || status === 'Closed';
  return checked ? 'Closed' : DEFAULT_CHECKLIST_STATUS;
}

const blankItem = (label: string): ActionChecklistItem => ({
  label,
  status: DEFAULT_CHECKLIST_STATUS,
  checked: false,
  comments: [],
});

function normalizeChecklistItem(value: unknown): ActionChecklistItem {
  if (typeof value === 'string') return blankItem(value);
  if (!value || typeof value !== 'object') return blankItem('');

  const row = value as Record<string, unknown>;
  const label = text(row.label) || text(row.text) || text(row.name);
  const status = checklistItemStatus({
    status: text(row.status),
    checked: typeof row.checked === 'boolean' ? row.checked : undefined,
    completed: typeof row.completed === 'boolean' ? row.completed : undefined,
  });
  const note = text(row.note);
  const attachments = normalizeChecklistAttachments(row.attachments);
  const responsible_individual_id = text(row.responsible_individual_id);
  const comments = normalizeComments(row.comments);

  return {
    label,
    status,
    checked: status === 'Closed',
    comments,
    ...(note ? { note } : {}),
    ...(attachments.length ? { attachments } : {}),
    ...(responsible_individual_id ? { responsible_individual_id } : {}),
  };
}

export function normalizeChecklistItems(value: unknown): ActionChecklistItem[] {
  return asRows(value).map(normalizeChecklistItem);
}

/**
 * Drops the empties rather than storing them: a blank note and a file row with
 * no id are both indistinguishable from absence once read back, and keeping
 * them would make `checklistItemExtraCount` overcount.
 */
/** A row about to be saved may still be an older shape with no status. */
export type ChecklistItemInput = Omit<
  ActionChecklistItem,
  'status' | 'comments'
> & {
  status?: ChecklistItemStatus;
  comments?: TaskComment[];
};

export const checklistForSubmit = (
  items: ChecklistItemInput[]
): ActionChecklistItem[] =>
  items.map((item) => {
    const note = (item.note ?? '').trim();
    const attachments = (item.attachments ?? []).filter((file) =>
      file.kind === 'folder' ? !!file.folderId : !!file.fileId
    );
    const responsible_individual_id = (
      item.responsible_individual_id ?? ''
    ).trim();
    const status = checklistItemStatus(item);
    return {
      label: item.label,
      status,
      checked: status === 'Closed',
      comments: commentsForSubmit(item.comments ?? []),
      ...(note ? { note } : {}),
      ...(attachments.length ? { attachments } : {}),
      ...(responsible_individual_id ? { responsible_individual_id } : {}),
    };
  });

/** Drives the collapsed row's badge, so extras are visible without expanding. */
export const checklistItemExtraCount = (item: ActionChecklistItem): number =>
  ((item.note ?? '').trim() ? 1 : 0) + (item.attachments ?? []).length;

export const checklistItemCommentCount = (item: ActionChecklistItem): number =>
  (item.comments ?? []).length;

export const hasChecklistExtras = (item: ActionChecklistItem): boolean =>
  checklistItemExtraCount(item) > 0;

export type SubTaskCompletion = {
  complete: number;
  total: number;
  percent: number;
};

/** Closed sub-tasks over the list. An empty list is 0 of 0. */
export function subTaskCompletion(
  items: ActionChecklistItem[]
): SubTaskCompletion {
  const total = items.length;
  const complete = items.filter(
    (item) => checklistItemStatus(item) === 'Closed'
  ).length;
  return {
    complete,
    total,
    percent: total === 0 ? 0 : Math.round((complete / total) * 100),
  };
}

export function setChecklistStatus(
  items: ActionChecklistItem[],
  index: number,
  status: ChecklistItemStatus
): ActionChecklistItem[] {
  return items.map((item, i) =>
    i === index ? { ...item, status, checked: status === 'Closed' } : item
  );
}

export function setChecklistNote(
  items: ActionChecklistItem[],
  index: number,
  note: string
): ActionChecklistItem[] {
  return items.map((item, i) => (i === index ? { ...item, note } : item));
}

export function setChecklistComments(
  items: ActionChecklistItem[],
  index: number,
  comments: TaskComment[]
): ActionChecklistItem[] {
  return items.map((item, i) => (i === index ? { ...item, comments } : item));
}

export function setChecklistAttachments(
  items: ActionChecklistItem[],
  index: number,
  attachments: ChecklistFileAttachment[]
): ActionChecklistItem[] {
  return items.map((item, i) =>
    i === index
      ? { ...item, attachments: attachments.slice(0, MAX_CHECKLIST_FILES) }
      : item
  );
}

export function removeChecklistAttachment(
  items: ActionChecklistItem[],
  index: number,
  attachmentKey: string
): ActionChecklistItem[] {
  return items.map((item, i) =>
    i === index
      ? {
          ...item,
          attachments: (item.attachments ?? []).filter(
            (file) => checklistAttachmentKey(file) !== attachmentKey
          ),
        }
      : item
  );
}

/**
 * Moves the sub-task at `fromIndex` to `toIndex`.
 *
 * Returns the original array when either index is out of range or the drop
 * landed on the row it started from, so callers can skip a pointless write.
 */
export function moveChecklistItem<T>(
  items: T[],
  fromIndex: number,
  toIndex: number
): T[] {
  if (
    !Number.isInteger(fromIndex) ||
    !Number.isInteger(toIndex) ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= items.length ||
    toIndex >= items.length ||
    fromIndex === toIndex
  ) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export function setChecklistResponsibleIndividual(
  items: ActionChecklistItem[],
  index: number,
  userId: string | null
): ActionChecklistItem[] {
  const nextId = (userId ?? '').trim();
  return items.map((item, i) => {
    if (i !== index) return item;
    if (!nextId) {
      const { responsible_individual_id: _dropped, ...rest } = item;
      return rest;
    }
    return { ...item, responsible_individual_id: nextId };
  });
}

/**
 * Drops a sub-task's person when they are no longer an occupant of the selected
 * seat. Leaves other extras alone.
 */
export function pruneChecklistResponsibleIndividuals(
  items: ActionChecklistItem[],
  allowedIds: Iterable<string>
): ActionChecklistItem[] {
  const allowed = allowedIds instanceof Set ? allowedIds : new Set(allowedIds);
  let changed = false;
  const next = items.map((item) => {
    if (
      !item.responsible_individual_id ||
      allowed.has(item.responsible_individual_id)
    ) {
      return item;
    }
    changed = true;
    const { responsible_individual_id: _dropped, ...rest } = item;
    return rest;
  });
  return changed ? next : items;
}
